import { fetchContent } from './api';
import { LINK_QUERY_FIELDS } from './fragments';

const getHeaderQuery = ({ locale = 'en' }) => `
  {
    globalModuleCollection(
      locale: "${locale}"
      limit: 1
      where: { name: "Header" }
    ) {
      items {
        resourcesCollection {
          items {
            ... on ComponentLink {
              __typename
              sys {
                id
              }
              name
              ${LINK_QUERY_FIELDS}
            }
            ... on ComponentMedia {
              __typename
              sys {
                id
              }
              name
              image {
                url
                width
                height
              }
            }
            ... on ComponentMicrocopy {
              __typename
              sys {
                id
              }
              name
              text
            }
            ... on ComponentNav {
              __typename
              sys {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const getNavQuery =
  ({ locale = 'en', preview = false }) =>
  id =>
    `
  {
    componentNav(id: "${id}", locale: "${locale}", preview: ${preview}) {
      __typename
      sys {
        id
      }
      name
      title
      link {
        __typename
        sys {
          id
        }
        ${LINK_QUERY_FIELDS}
      }
      showMobileLink
      expandSubmenuMobile
      linksCollection(limit: 20) {
        items {
          ... on ComponentLink {
            __typename
            sys {
              id
            }
            name
            ${LINK_QUERY_FIELDS}
          }
          ... on ComponentNav {
            __typename
            sys {
              id
            }
          }
        }
      }
      bannersCollection(limit: 3) {
        items {
          sys {
            id
          }
          image {
            url
            width
            height
          }
          mobileImage {
            url
            width
            height
          }
          title
          subtitle
          link {
            ${LINK_QUERY_FIELDS}
          }
        }
      }
      emphasisColor
    }
  }
`;

const fetchNavsFrom = async ({ items, locale = 'en', preview = false }) =>
  (
    await Promise.all(
      items
        .filter(Boolean)
        .filter(({ __typename: type }) => type === 'ComponentNav')
        .map(item => item.sys.id)
        .map(getNavQuery({ locale, preview }))
        .map(qry => fetchContent(qry, preview))
    )
  ).map(({ componentNav }) => componentNav);

const mixinNavFrom =
  navsById =>
  ({ __typename, sys, ...rest }) => {
    if (__typename !== 'ComponentNav') return { __typename, sys, ...rest };

    const nav = navsById[sys.id];

    return {
      ...nav,
      linksCollection: {
        items: nav.linksCollection.items.filter(Boolean).map(mixinNavFrom(navsById)),
      },
    };
  };

const fetchHeader = async ({ locale = 'en', preview = false }) => {
  const header = await fetchContent(getHeaderQuery({ locale }), preview);
  const { items: resources } = header?.globalModuleCollection?.items[0]?.resourcesCollection;

  const navsL1 = await fetchNavsFrom({ items: resources, locale, preview });
  const navsL2 = await fetchNavsFrom({
    items: navsL1.flatMap(({ linksCollection }) => linksCollection.items),
    locale,
    preview,
  });
  const navsL3 = await fetchNavsFrom({
    items: navsL2.flatMap(({ linksCollection }) => linksCollection.items),
    locale,
    preview,
  });

  const navsById = [...navsL1, ...navsL2, ...navsL3].reduce(
    (acc, nav) => ({ ...acc, [nav.sys.id]: nav }),
    {}
  );

  const resourcesWithNavs = resources.map(mixinNavFrom(navsById)).filter(Boolean);

  return resourcesWithNavs;
};

export default fetchHeader;
